import { lessonSource } from '../../../library/printing/cardReadiness';
import { PortfolioUniverseCards } from '../../../library/printing/PortfolioUniverseCards';
import { useSquadUniverseCards } from '../../../library/printing/SquadUniverseCards';
import React, { useEffect, useMemo, useState } from 'react';
import {
  CLASSROOM_TEAM_COLORS,
  ClassroomTeam,
  PortfolioAiCreationType,
  PortfolioAiVersion,
  PortfolioEntry,
  buildPlanSessionId,
  archivedAiCreations,
  visibleAiCreations,
} from '../../classroom/types';
import { ClassroomPortfolioService } from '../../classroom/ClassroomPortfolioService';
import { formatSlotDate, ResolvedSessionPlan } from '../../utils/sessionPlan';
import {
  AI_CREATION_TYPE_LABELS,
  fetchBytes,
  photoExtension,
  portfolioPhotoFilename,
  portfolioDownloadStem,
  provenanceLine,
  rangeSelectIds,
  triggerBlobDownload,
  videoEmbedUrl,
  creationDisplayName,
  websitePreviewUrl,
} from '../../classroom/aiCreation';
import { zipStoreFiles } from '../../classroom/zipStore';
import AiGroupBoard from './AiGroupBoard';
import AiGroupEditor from './AiGroupEditor';
import { shotBatchForNomination } from '../../../library/shotBatchNomination';
import { LibraryItem } from '../../../library/types';
import ShowdownCreationModal from './ShowdownCreationModal';
import { useAuth } from '../../../auth/contexts/AuthContext';

interface PortfolioEntryCardProps {
  squadId: string;
  squadName: string;
  team: ClassroomTeam;
  teams: ClassroomTeam[];
  entry: PortfolioEntry;
  plan: ResolvedSessionPlan;
  onAddPhotos: (slotIndex: number | null) => void;
  onEditNote: () => void;
  onAddAiCreation: (sourcePhotoIds: string[]) => void;
  onReplayReveal: () => void;
}

type ViewerState =
  | { kind: 'image'; src: string; title: string; uploadedAt?: unknown; creation?: PortfolioAiVersion }
  | { kind: 'audio'; src: string; title: string; creation?: PortfolioAiVersion }
  | { kind: 'video'; src: string; title: string; embed?: boolean; creation?: PortfolioAiVersion };

const uploadTimestampLabel = (value: unknown): string => {
  const raw = (value as any)?.toDate?.() || value;
  const numeric = typeof raw === 'number' && raw > 0 && raw < 1_000_000_000_000 ? raw * 1000 : raw;
  const date = raw instanceof Date ? raw : numeric ? new Date(numeric as any) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  return `Uploaded ${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} at ${date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
};

const TYPE_BADGE: Record<PortfolioAiCreationType, string> = {
  image: 'IMG',
  video: '▶',
  audio: '♫',
  pdf: 'PDF',
  game_link: 'GAME',
  claude_artifact: '✦',
  model_3d: '3D',
  link: '↗',
};

function GenTilePoster({ gen }: { gen: PortfolioAiVersion }) {
  const [broken, setBroken] = useState(false);
  const label = creationDisplayName({
    filename: gen.filename,
    url: gen.url,
    caption: gen.caption,
    type: gen.type,
  });
  const poster =
    gen.posterUrl ||
    (gen.type !== 'image' && gen.type !== 'pdf' && gen.type !== 'audio' ? websitePreviewUrl(gen.url || '') : null);
  if (poster && !broken) {
    return <img src={poster} alt="" onError={() => setBroken(true)} />;
  }
  return (
    <span className="cd-shot-filename" title={label}>
      {label}
    </span>
  );
}

function GenShot({
  gen,
  index,
  photos,
  selected,
  onToggle,
  onOpen,
  archived,
}: {
  gen: PortfolioAiVersion;
  index: number;
  photos: PortfolioEntry['photos'];
  selected: boolean;
  onToggle: (id: string, shiftKey: boolean) => void;
  onOpen: (gen: PortfolioAiVersion) => void;
  archived?: boolean;
}) {
  return (
    <div className={`cd-gen ${archived ? 'is-archived' : ''}`}>
      <div className={`cd-shot cd-shot-gen ${selected ? 'is-selected' : ''} ${archived ? 'is-archived' : ''}`}>
        <label className="cd-shot-check">
          <input
            type="checkbox"
            checked={selected}
            onChange={(event) => {
              onToggle(gen.id, Boolean((event.nativeEvent as MouseEvent).shiftKey));
            }}
            onClick={(event) => event.stopPropagation()}
            aria-label={`${selected ? 'Unselect' : 'Select'} AI creation ${index + 1}`}
          />
        </label>
        <button
          type="button"
          className="cd-shot-hit"
          onClick={(event) => {
            if (event.shiftKey) {
              onToggle(gen.id, true);
              return;
            }
            onOpen(gen);
          }}
          title={gen.caption || 'Open AI creation'}
        >
          <GenTilePoster gen={gen} />
          <span className="cd-shot-type">{TYPE_BADGE[gen.type || 'link']}</span>
        </button>
      </div>
      <p className="cd-gen-prov">{provenanceLine({ photos, gen })}</p>
      {gen.caption ? <p className="cd-gen-caption">{gen.caption}</p> : null}
    </div>
  );
}

const PortfolioEntryCard: React.FC<PortfolioEntryCardProps> = ({
  squadId,
  squadName,
  team,
  teams,
  entry,
  plan,
  onAddPhotos,
  onEditNote,
  onAddAiCreation,
  onReplayReveal,
}) => {
  const { currentUser, userData } = useAuth();
  const allPhotos = entry.photos || [];
  const photos = allPhotos.filter((photo) => !photo.archived);
  const archivedPhotos = allPhotos.filter((photo) => photo.archived);
  const gens = visibleAiCreations(entry.aiVersions);
  const archivedGens = archivedAiCreations(entry.aiVersions);
  const photoIds = useMemo(() => photos.map((p) => p.id), [photos]);
  const visibleGenIds = useMemo(() => gens.map((g) => g.id), [gens]);
  const archivedGenIds = useMemo(() => archivedGens.map((g) => g.id), [archivedGens]);
  const [groupEditorOpen, setGroupEditorOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedGenIds, setSelectedGenIds] = useState<string[]>([]);
  const [anchorId, setAnchorId] = useState<string | null>(null);
  const [genAnchorId, setGenAnchorId] = useState<string | null>(null);
  const [busyDownload, setBusyDownload] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTeamId, setMoveTeamId] = useState(team.id);
  const [moveSlotIndex, setMoveSlotIndex] = useState(entry.slotIndex || plan.slots[0]?.index || 1);
  const [viewer, setViewer] = useState<ViewerState | null>(null);
  const [nominationBatch, setNominationBatch] = useState<LibraryItem['shotBatch']>();
  const [showdownCreation, setShowdownCreation] = useState<PortfolioAiVersion | null>(null);
  const [libraryStatuses, setLibraryStatuses] = useState<Record<string, string>>({});

  const { cards: squadCards } = useSquadUniverseCards();
  useEffect(() => {
    const next: Record<string, string> = {};
    squadCards.forEach(card => {
      if (card.sourcePortfolioCreationId && card.status !== 'archived') next[card.sourcePortfolioCreationId] = card.status;
    });
    setLibraryStatuses(next);
  }, [squadCards]);

  const libraryStatusLabel = (creationId: string) => {
    const status = libraryStatuses[creationId];
    if (status === 'review') return 'In review';
    if (status === 'published') return 'Published';
    return status ? 'Nominated' : '';
  };

  const accent =
    (CLASSROOM_TEAM_COLORS[team.colorKey] || CLASSROOM_TEAM_COLORS.reef).accent;

  const statusPill =
    entry.status === 'revealed'
      ? { cls: 'cd-pill-done', text: 'Revealed' }
      : gens.length
        ? { cls: 'cd-pill-reveal', text: '✦ Ready to reveal' }
        : photos.length
          ? { cls: 'cd-pill-needs', text: 'Needs AI creation' }
          : { cls: 'cd-pill-muted', text: 'Draft' };

  const slotIdx =
    typeof entry.slotIndex === 'number'
      ? plan.slots.findIndex((s) => s.index === entry.slotIndex)
      : plan.slots.findIndex((s) => s.levelId === entry.levelId);

  const selected = photos.filter((p) => selectedIds.includes(p.id));
  const selectedVisibleGens = gens.filter((g) => selectedGenIds.includes(g.id));
  const selectedArchivedGens = archivedGens.filter((g) => selectedGenIds.includes(g.id));

  const togglePhoto = (photoId: string, shiftKey: boolean) => {
    if (shiftKey) {
      const range = rangeSelectIds(photoIds, anchorId, photoId);
      setSelectedIds(range);
      setAnchorId(photoId);
      return;
    }
    setSelectedIds((current) =>
      current.includes(photoId) ? current.filter((id) => id !== photoId) : [...current, photoId]
    );
    setAnchorId(photoId);
  };

  const toggleGen = (genId: string, shiftKey: boolean) => {
    const list = visibleGenIds.includes(genId) ? visibleGenIds : archivedGenIds;
    if (shiftKey) {
      const range = rangeSelectIds(list, genAnchorId, genId);
      setSelectedGenIds(range);
      setGenAnchorId(genId);
      return;
    }
    setSelectedGenIds((current) =>
      current.includes(genId) ? current.filter((id) => id !== genId) : [...current, genId]
    );
    setGenAnchorId(genId);
  };

  const setArchived = async (genIds: string[], archived: boolean) => {
    if (!genIds.length) return;
    setArchiveBusy(true);
    try {
      await ClassroomPortfolioService.setAiCreationsArchived({
        squadId,
        entryId: entry.id,
        photos: entry.photos,
        aiVersions: entry.aiVersions || [],
        genIds,
        archived,
      });
      setSelectedGenIds((current) => current.filter((id) => !genIds.includes(id)));
    } catch (error) {
      console.error(error);
      void window.appAlert(archived ? 'Could not archive AI creations.' : 'Could not restore AI creations.');
    } finally {
      setArchiveBusy(false);
    }
  };

  const setPhotosArchived = async (photoIds: string[], archived: boolean) => {
    setPhotoBusy(true);
    try {
      await ClassroomPortfolioService.setPhotosArchived({
        squadId,
        entryId: entry.id,
        photoIds,
        archived,
      });
      setSelectedIds([]);
    } catch (error) {
      console.error(error);
      void window.appAlert(archived ? 'Could not archive photos.' : 'Could not restore photos.');
    } finally {
      setPhotoBusy(false);
    }
  };

  const moveSelectedPhotos = async () => {
    const destinationTeam = teams.find((item) => item.id === moveTeamId);
    const slot = plan.slots.find((item) => item.index === moveSlotIndex);
    if (!destinationTeam || !slot || !selectedIds.length) return;
    setPhotoBusy(true);
    try {
      await ClassroomPortfolioService.movePhotos({
        squadId,
        sourceEntryId: entry.id,
        photoIds: selectedIds,
        destinationTeamId: destinationTeam.id,
        destinationMemberIdsSnapshot: destinationTeam.memberIds,
        destinationSession: {
          sessionId: buildPlanSessionId({
            levelId: slot.levelId,
            date: slot.date,
            slotIndex: slot.index,
          }),
          levelId: slot.levelId,
          levelTitle: slot.levelTitle,
          slotIndex: slot.index,
          date: slot.date,
        },
      });
      setSelectedIds([]);
      setMoveOpen(false);
    } catch (error) {
      console.error(error);
      void window.appAlert('Could not move the selected photos.');
    } finally {
      setPhotoBusy(false);
    }
  };

  const openGen = (gen: PortfolioAiVersion) => {
    const type = gen.type || 'link';
    const title = gen.caption || AI_CREATION_TYPE_LABELS[type];
    if (type === 'image') {
      setViewer({ kind: 'image', src: gen.url || gen.posterUrl || '', title, creation: gen });
      return;
    }
    if (type === 'audio' && gen.url) { setViewer({ kind: 'audio', src: gen.url, title, creation: gen }); return; }
    if (type === 'video') {
      const embed = gen.url ? videoEmbedUrl(gen.url) : null;
      if (embed) {
        setViewer({ kind: 'video', src: embed, title, embed: true, creation: gen });
        return;
      }
      if (gen.url) {
        setViewer({ kind: 'video', src: gen.url, title, creation: gen });
        return;
      }
    }
    if (gen.url) window.open(gen.url, '_blank', 'noopener,noreferrer');
  };

  const downloadSelected = async () => {
    if (!selected.length) return;
    setBusyDownload(true);
    try {
      const files = await Promise.all(
        selected.map(async (photo) => {
          const index = photos.findIndex((item) => item.id === photo.id) + 1;
          const name = portfolioPhotoFilename({
            teamName: team.name,
            squadName,
            classDate: entry.date,
            className: entry.levelTitle,
            photoIndex: index,
            ext: photoExtension(photo),
          });
          const bytes = await fetchBytes(photo.url);
          return { name, bytes };
        })
      );
      if (files.length === 1) {
        triggerBlobDownload(
          new Blob([files[0].bytes]),
          files[0].name
        );
      } else {
        triggerBlobDownload(
          zipStoreFiles(files),
          `${portfolioDownloadStem({
            teamName: team.name,
            squadName,
            classDate: entry.date,
            className: entry.levelTitle,
          })}_photos.zip`
        );
      }
    } catch (error) {
      console.error(error);
      void window.appAlert('Could not download the original photos. Try again, or open a photo and save it from the browser.');
    } finally {
      setBusyDownload(false);
    }
  };

  return (
    <div className="cd-entry" style={{ borderLeftColor: accent }}>
      <div className="cd-entry-head">
        <div>
          <span className="cd-entry-name">
            {entry.slotIndex ? `Lesson ${entry.slotIndex} · ` : ''}
            {entry.levelTitle}
          </span>
          {entry.date ? (
            <span className="cd-entry-date"> {formatSlotDate(entry.date)}</span>
          ) : null}
        </div>
        <span className={`cd-pill ${statusPill.cls}`}>{statusPill.text}</span>
      </div>

      {archivedPhotos.length > 0 && (
        <details className="cd-archived-gens">
          <summary>
            Archived photos ({archivedPhotos.length}) — hidden, still saved
          </summary>
          <div className="cd-shots-paper">
            {archivedPhotos.map((photo, index) => (
              <div key={photo.id} className="cd-shot is-archived">
                <button
                  type="button"
                  className="cd-shot-hit"
                  onClick={() => setViewer({ kind: 'image', src: photo.url, title: `Archived photo ${index + 1}`, uploadedAt: photo.uploadedAt })}
                >
                  <img src={photo.url} alt={`Archived work ${index + 1}`} />
                  <span>archived photo {index + 1}</span>
                </button>
                <button
                  type="button"
                  className="cd-photo-restore"
                  disabled={photoBusy}
                  onClick={() => void setPhotosArchived([photo.id], false)}
                >
                  Restore
                </button>
              </div>
            ))}
          </div>
        </details>
      )}

      <div className="cd-shots">
        <div className="cd-shots-paper">
          {photos.map((photo, index) => {
            const isOn = selectedIds.includes(photo.id);
            return (
              <div
                key={photo.id}
                className={`cd-shot ${isOn ? 'is-selected' : ''}`}
              >
                <label className="cd-shot-check">
                  <input
                    type="checkbox"
                    checked={isOn}
                    onChange={(event) => {
                      togglePhoto(
                        photo.id,
                        Boolean((event.nativeEvent as MouseEvent).shiftKey)
                      );
                    }}
                    onClick={(event) => event.stopPropagation()}
                    aria-label={`Select photo ${index + 1}`}
                  />
                </label>
                <button
                  type="button"
                  className="cd-shot-hit"
                  title={`Photo ${index + 1}`}
                  onClick={(event) => {
                    if (event.shiftKey) {
                      togglePhoto(photo.id, true);
                      return;
                    }
                    setViewer({ kind: 'image', src: photo.url, title: `Photo ${index + 1}`, uploadedAt: photo.uploadedAt });
                  }}
                >
                  <img src={photo.url} alt={`Uploaded work ${index + 1}`} />
                  <span>photo {index + 1}</span>
                </button>
              </div>
            );
          })}
        </div>

        {photos.length > 0 && (
          <>
            <span className="cd-shot-arrow" aria-hidden="true">
              →
            </span>
            <div className="cd-shots-ai">
              {gens.filter(gen => !entry.aiGroups?.some(g => g.id === gen.groupId)).map((gen, index) => (
                <GenShot
                  key={gen.id}
                  gen={gen}
                  index={index}
                  photos={photos}
                  selected={selectedGenIds.includes(gen.id)}
                  onToggle={toggleGen}
                  onOpen={openGen}
                />
              ))}
              <button
                type="button"
                className="cd-shot cd-shot-add"
                onClick={() => onAddAiCreation(selectedIds.length ? selectedIds : photos.map((p) => p.id))}
              >
                <span className="cd-shot-big">+</span>
                <span>Add</span>
              </button>
            </div>
          </>
        )}
      </div>

      <PortfolioUniverseCards entry={entry} />
      {!!entry.aiGroups?.length && <AiGroupBoard squadId={squadId} entry={entry} onEdit={() => setGroupEditorOpen(true)}
        onNominateOption={creation => { setNominationBatch(undefined); setShowdownCreation(creation); }}
        onArchive={ids => setArchived(ids, true)} archiveBusy={archiveBusy}
        nominationStatuses={libraryStatuses}
        batchNominated={Boolean(libraryStatuses[`shot_batch_${entry.id}`])}
        onNominateBatch={() => {
          try {
            const batch = shotBatchForNomination(entry);
            setNominationBatch(batch);
            setShowdownCreation({ id: `shot_batch_${entry.id}`, type: 'video', url: batch.videos[0].url, caption: `${team.name} — shot batch`, status: 'approved', createdAt: Date.now() });
          } catch (error: any) { void window.appAlert(error.message); }
        }} />}
      {groupEditorOpen && <AiGroupEditor squadId={squadId} entry={entry} sourcePhotoIds={photos.map(p => p.id)} mode="manage" onClose={() => setGroupEditorOpen(false)} />}

      {archivedGens.length > 0 && (
        <details className="cd-archived-gens">
          <summary>
            Archived AI creations ({archivedGens.length}) — hidden from kids, still saved
          </summary>
          <div className="cd-shots-ai">
            {archivedGens.map((gen, index) => (
              <GenShot
                key={gen.id}
                gen={gen}
                index={index}
                photos={photos}
                selected={selectedGenIds.includes(gen.id)}
                onToggle={toggleGen}
                onOpen={openGen}
                archived
              />
            ))}
          </div>
        </details>
      )}

      {!!entry.aiGroups?.length && <details className="cd-archived-gens"><summary>Manage individual options (archive or nominate)</summary><div className="cd-shots-ai">{gens.filter(gen => entry.aiGroups?.some(g => g.id === gen.groupId)).map((gen, index) => <GenShot key={gen.id} gen={gen} index={index} photos={photos} selected={selectedGenIds.includes(gen.id)} onToggle={toggleGen} onOpen={openGen} />)}</div></details>}
      {entry.teacherNote ? <div className="cd-enote">“{entry.teacherNote}”</div> : null}

      {selected.length > 0 && (
        <div className="cd-entry-select-bar">
          <span>
            {selected.length} photo{selected.length === 1 ? '' : 's'} selected
          </span>
          <button
            type="button"
            className="cd-btn cd-btn-compact"
            disabled={busyDownload}
            onClick={() => void downloadSelected()}
          >
            {busyDownload ? 'Preparing…' : 'Download'}
          </button>
          <button
            type="button"
            className="cd-btn cd-btn-compact cd-btn-primary"
            onClick={() => onAddAiCreation(selectedIds)}
          >
            Use for AI creation
          </button>
          <button
            type="button"
            className="cd-btn cd-btn-compact"
            disabled={photoBusy}
            onClick={() => {
              setMoveTeamId(team.id);
              setMoveSlotIndex(entry.slotIndex || plan.slots[0]?.index || 1);
              setMoveOpen(true);
            }}
          >
            Move to…
          </button>
          <button
            type="button"
            className="cd-btn cd-btn-compact"
            disabled={photoBusy}
            onClick={() => void setPhotosArchived(selectedIds, true)}
          >
            {photoBusy ? 'Saving…' : 'Archive'}
          </button>
          <button
            type="button"
            className="cd-btn cd-btn-ghost cd-btn-compact"
            onClick={() => setSelectedIds([])}
          >
            Clear
          </button>
        </div>
      )}

      {(selectedVisibleGens.length > 0 || selectedArchivedGens.length > 0) && (
        <div className="cd-entry-select-bar cd-entry-select-bar-gens">
          <span>
            {selectedGenIds.length} AI creation{selectedGenIds.length === 1 ? '' : 's'} selected
          </span>
          {selectedVisibleGens.length > 0 && (
            <button
              type="button"
              className="cd-btn cd-btn-compact cd-btn-primary"
              disabled={selectedVisibleGens.length !== 1 || Boolean(selectedVisibleGens[0] && libraryStatuses[selectedVisibleGens[0].id])}
              title={selectedVisibleGens.length === 1 ? (libraryStatusLabel(selectedVisibleGens[0].id) || 'Nominate for the Universe') : 'Select one creation at a time'}
              onClick={() => setShowdownCreation(selectedVisibleGens[0])}
            >
              {selectedVisibleGens.length === 1 && libraryStatusLabel(selectedVisibleGens[0].id) ? `✓ ${libraryStatusLabel(selectedVisibleGens[0].id)}` : '📖 Nominate for the Universe'}
            </button>
          )}
          {selectedVisibleGens.length > 0 && (
            <button
              type="button"
              className="cd-btn cd-btn-compact"
              disabled={archiveBusy}
              onClick={() => void setArchived(selectedVisibleGens.map((g) => g.id), true)}
            >
              {archiveBusy ? 'Saving…' : 'Archive'}
            </button>
          )}
          {selectedArchivedGens.length > 0 && (
            <button
              type="button"
              className="cd-btn cd-btn-compact cd-btn-primary"
              disabled={archiveBusy}
              onClick={() => void setArchived(selectedArchivedGens.map((g) => g.id), false)}
            >
              {archiveBusy ? 'Saving…' : 'Restore'}
            </button>
          )}
          <button
            type="button"
            className="cd-btn cd-btn-ghost cd-btn-compact"
            onClick={() => setSelectedGenIds([])}
          >
            Clear
          </button>
        </div>
      )}

      <div className="cd-entry-actions">
        <button
          type="button"
          className="cd-btn cd-btn-compact"
          onClick={() => onAddPhotos(slotIdx >= 0 ? slotIdx : null)}
        >
          📷 Add photos to this lesson
        </button>
        {photos.length > 0 && (
          <button
            type="button"
            className="cd-btn cd-btn-compact"
            onClick={() => onAddAiCreation(selectedIds.length ? selectedIds : photos.map((p) => p.id))}
          >
            Add AI creation
          </button>
        )}
        {gens.length > 0 && !entry.aiGroups?.length && entry.status !== 'revealed' && (
          <button
            type="button"
            className="cd-btn"
            disabled={statusBusy}
            onClick={async () => {
              setStatusBusy(true);
              try {
                await ClassroomPortfolioService.markRevealed({
                  squadId,
                  entryId: entry.id,
                  photos: entry.photos,
                  aiVersions: entry.aiVersions || [],
                });
              } catch (error) {
                console.error(error);
                void window.appAlert('Could not mark revealed');
              } finally {
                setStatusBusy(false);
              }
            }}
          >
            Mark revealed
          </button>
        )}
        {entry.status === 'revealed' && (
          <button type="button" className="cd-btn cd-btn-compact cd-btn-primary" onClick={onReplayReveal}>
            ✦ Replay reveal
          </button>
        )}
        {entry.status === 'revealed' && (
          <button
            type="button"
            className="cd-btn"
            disabled={statusBusy}
            onClick={async () => {
              setStatusBusy(true);
              try {
                await ClassroomPortfolioService.revertToAiReady({
                  squadId,
                  entryId: entry.id,
                  photos: entry.photos,
                  aiVersions: entry.aiVersions || [],
                });
              } catch (error) {
                console.error(error);
                void window.appAlert('Could not revert to AI ready');
              } finally {
                setStatusBusy(false);
              }
            }}
          >
            Back to AI ready
          </button>
        )}
        <button type="button" className="cd-btn cd-btn-ghost" onClick={onEditNote}>
          {entry.teacherNote ? 'Edit note' : 'Add note'}
        </button>
      </div>

      {viewer && (
        <div
          className="cd-lightbox"
          onClick={() => setViewer(null)}
          role="presentation"
        >
          <div
            className="cd-lightbox-card"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-label={viewer.title}
          >
            <div className="cd-lightbox-head">
              <div className="cd-lightbox-title"><strong>{viewer.title}</strong>{viewer.kind === 'image' && uploadTimestampLabel(viewer.uploadedAt) && <small>{uploadTimestampLabel(viewer.uploadedAt)}</small>}</div>
              <div className="cd-lightbox-tools">
                {viewer.creation && (
                  <button
                    type="button"
                    className="cd-btn cd-btn-compact cd-btn-primary"
                    disabled={Boolean(libraryStatuses[viewer.creation.id])}
                    onClick={() => {
                      setShowdownCreation(viewer.creation || null);
                      setViewer(null);
                    }}
                  >
                    {libraryStatusLabel(viewer.creation.id) ? `✓ ${libraryStatusLabel(viewer.creation.id)}` : '📖 Nominate for the Universe'}
                  </button>
                )}
                <button type="button" className="cd-modal-close" onClick={() => setViewer(null)}>×</button>
              </div>
            </div>
            {viewer.kind === 'audio' && <audio src={viewer.src} controls preload="metadata" aria-label={viewer.title} />}
            {viewer.kind === 'image' && <img src={viewer.src} alt={viewer.title} />}
            {viewer.kind === 'video' && viewer.embed && (
              <iframe
                title={viewer.title}
                src={viewer.src}
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
            )}
            {viewer.kind === 'video' && !viewer.embed && (
              <video src={viewer.src} controls autoPlay playsInline />
            )}
          </div>
        </div>
      )}

      {moveOpen && (
        <div className="cd-lightbox" role="presentation" onClick={() => setMoveOpen(false)}>
          <form
            className="cd-photo-move-card"
            onSubmit={(event) => {
              event.preventDefault();
              void moveSelectedPhotos();
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="cd-lightbox-head">
              <div>
                <strong>Move {selectedIds.length} photo{selectedIds.length === 1 ? '' : 's'}</strong>
                <p>Choose the team and lesson where this work belongs.</p>
              </div>
              <button type="button" className="cd-modal-close" onClick={() => setMoveOpen(false)}>×</button>
            </div>
            <label>
              Team
              <select value={moveTeamId} onChange={(event) => setMoveTeamId(event.target.value)}>
                {teams.filter((item) => !item.archived).map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            </label>
            <label>
              Lesson
              <select value={moveSlotIndex} onChange={(event) => setMoveSlotIndex(Number(event.target.value))}>
                {plan.slots.map((slot) => (
                  <option key={`${slot.index}-${slot.levelId}`} value={slot.index}>
                    Lesson {slot.index} · {slot.levelTitle}{slot.date ? ` · ${formatSlotDate(slot.date)}` : ''}
                  </option>
                ))}
              </select>
            </label>
            <div className="cd-photo-move-actions">
              <button type="button" className="cd-btn" onClick={() => setMoveOpen(false)}>Cancel</button>
              <button type="submit" className="cd-btn cd-btn-primary" disabled={photoBusy}>
                {photoBusy ? 'Moving…' : 'Move photos'}
              </button>
            </div>
          </form>
        </div>
      )}
      <ShowdownCreationModal
        source={lessonSource(entry)}
        isOpen={!!showdownCreation}
        onClose={() => { setShowdownCreation(null); setNominationBatch(undefined); }}
        shotBatch={nominationBatch}
        creation={showdownCreation}
        squadId={squadId}
        squadName={squadName}
        team={team}
        photos={photos}
        existingStatus={showdownCreation ? libraryStatuses[showdownCreation.id] : undefined}
        onNominated={(creationId) => setLibraryStatuses(current => ({ ...current, [creationId]: 'nominated' }))}
      />
    </div>
  );
};

export default PortfolioEntryCard;
